import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import { AppStatusEnum } from "@verbleif/lib/src/composables/Api/Type/AppStore/app.enum";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { isImpersonating } from "../../Impersonation/impersonationState";
import { isCmsContext } from "../../isCms";
import { ContextVoter } from "./composables/ContextVoter";

export class AppVoter extends ContextVoter implements ContextVoterInterface {
  // CMS-only ADVANCED-FIELD gate. The web-vs-CMS split is CONTEXTUAL, never permission-scope based: an app
  // developer edits their app on the WEB — create/update/delete, connection config, secrets, inbound
  // auth-method — regardless of whether their grant is client- or global-scoped. Those capabilities resolve
  // like the backend (global `*_any`/override OR client-scoped, no CMS gate — see canCreate/canWriteData/
  // canManageSecrets). ONLY the Verbleif-curated advanced fields — status, type, category, rate limit,
  // internal identifier and the FULL auth config (auth_config: "none" + connector documents + data-type
  // selects) — are locked to the CMS shell. Those field gates (and only those) route their global permission
  // through here, so they stay hidden on the web app store even for a holder of the global override.
  private hasGlobalAppPermission(attribute: PermissionAttributesEnum | PermissionAttributesEnum[]): boolean {
    if (!isCmsContext()) {
      return false;
    }

    return !!this.permissionService.getGlobalPermissionForUser({ attribute });
  }

  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    return this._canCreate({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationAppCreateany,
      contextAttribute: PermissionAttributesEnum.ContextClientOperationAppCreate,
    });
  };

  public canRead: ContextVoterInterface<{ status?: AppStatusEnum }>["canRead"] = (item) => {
    // Mirror backend AppVoter::canRead ordering exactly. Global READ_ANY is NOT CMS-gated: GET /api/apps/{id}
    // honours it in the web app store / integrations shell, like the create/update/delete overrides. Only the
    // advanced field gates (status/type/category/rate limit/internal id/auth config) stay CMS-only, via
    // hasGlobalAppPermission. Bespoke ordering (not _canCrud) keeps the PUBLISHED fast-path below and avoids
    // the list-vs-view regression where the catalog returned an app but AppStoreItem blocked opening it.
    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationAppReadany,
    })) {
      return true;
    }

    if (item.status === AppStatusEnum.PUBLISHED) {
      return true;
    }

    return this.hasAccess({
      "@id": item["@id"],
      "location": item.location,
      "client": item.client,
      "attribute": PermissionAttributesEnum.ContextClientOperationAppRead,
    });
  };

  public canDelete: ContextVoterInterface["canDelete"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationAppDeleteany,
      contextAttribute: PermissionAttributesEnum.ContextClientOperationAppDelete,
    });
  };

  public canUpdate: ContextVoterInterface["canUpdate"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationAppUpdateany,
      contextAttribute: PermissionAttributesEnum.ContextClientOperationAppUpdate,
    });
  };

  public canManage: AggregateVoterCheck = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: [
        PermissionAttributesEnum.GlobalOperationAppCreateany,
        PermissionAttributesEnum.GlobalOperationAppUpdateany,
        PermissionAttributesEnum.GlobalOperationAppDeleteany,
      ],
      contextAttribute: [
        PermissionAttributesEnum.ContextClientOperationAppCreate,
        PermissionAttributesEnum.ContextClientOperationAppUpdate,
        PermissionAttributesEnum.ContextClientOperationAppDelete,
      ],
    });
  };

  // Config `data` field write. CONTEXT (client) scoped: a client's app manager may edit their own client's
  // app config, while the platform-admin GlobalAppWriteFieldData override writes any app's data regardless of
  // client. Pass the app's client IRI so the client-scoped grant resolves; omitting it checks the global
  // override only. Mirrors the backend AppFieldVoter (DATA_WRITE_ANY OR hasAccessVia(client, DATA_WRITE)).
  public canWriteData = (clientIri?: string | null): boolean => {
    // Config `data` is web-editable (connection details), NOT a CMS-only advanced field, so the global
    // override applies everywhere — exactly like the backend DATA_WRITE_ANY. Do NOT route it through the
    // CMS-only hasGlobalAppPermission gate.
    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalAppWriteFieldData,
    })) {
      return true;
    }

    if (!clientIri) {
      return false;
    }

    return this.permissionService.hasAccessVia({
      iri: clientIri,
      attribute: PermissionAttributesEnum.ContextClientAppWriteFieldData,
    });
  };

  // Dedicated secret-management gate, SEPARATE from canWriteData (config editing): revealing / rotating an
  // app's confidential secrets (inbound-OAuth client id/secret, webhook signing secret, per-install inbound
  // API key). CONTEXT (client) scoped so a client's app manager can self-serve their own client's secrets;
  // the platform-admin GlobalAppSecretManage override reaches any app's secrets. Mirrors the backend
  // AppSecretVoter (SECRET_MANAGE_ANY OR hasAccessVia(client, SECRET_MANAGE)).
  public canManageSecrets = (clientIri?: string | null): boolean => {
    // Staff signed in as another person never reveal or renew that person's secrets (the API refuses it).
    if (isImpersonating.value) {
      return false;
    }

    // Secret reveal/rotate is web-editable (connection details / signing secrets), NOT a CMS-only advanced
    // field, so the global override applies everywhere — exactly like the backend SECRET_MANAGE_ANY. Do NOT
    // route it through the CMS-only hasGlobalAppPermission gate.
    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalAppSecretManage,
    })) {
      return true;
    }

    if (!clientIri) {
      return false;
    }

    return this.permissionService.hasAccessVia({
      iri: clientIri,
      attribute: PermissionAttributesEnum.ContextClientAppSecretManage,
    });
  };

  // Platform-admin-only rate-limit configuration, CMS only (the web App Store never shows it, also not to
  // admins). Reading (the app policy, the per-install override, the platform default) and writing are separate
  // global grants, mirroring the backend AppAttribute::RATE_LIMIT_READ / RATE_LIMIT_WRITE; the API only returns
  // the configuration to RATE_LIMIT_READ holders. Everyone else sees the app's read-only `rateLimitSummary`.
  public canReadRateLimit = (): boolean => {
    return this.hasGlobalAppPermission(PermissionAttributesEnum.GlobalAppReadFieldRatelimit);
  };

  public canWriteRateLimit = (): boolean => {
    return this.hasGlobalAppPermission(PermissionAttributesEnum.GlobalAppWriteFieldRatelimit);
  };

  // Platform-admin-only whole-tab authentication-configuration gate (AUTH_CONFIG_WRITE): the inbound auth
  // method, redirect URIs, requested scopes, the outbound authData secret and the integration data-type
  // selects that ride the Authentication tab. Deliberately GLOBAL with no client-scoped grant (unlike
  // canWriteData / canManageSecrets): an app owner/org manager must not reconfigure how their app
  // authenticates against the platform. Mirrors the backend AppAttribute::AUTH_CONFIG_WRITE field
  // permission, which superseded the former global auth_data one.
  public canConfigureAuth = (): boolean => {
    return this.hasGlobalAppPermission(PermissionAttributesEnum.GlobalAppWriteFieldAuthconfig);
  };

  // Restricted inbound auth-METHOD choice (AUTH_METHOD_WRITE), the app-developer counterpart to the
  // platform-admin canConfigureAuth: it lets an app owner pick the inbound method (API key / OAuth2) for
  // THEIR app, but never reach "none", the inbound/outbound connector documents or the integration data-type
  // selects, which stay canConfigureAuth. Deliberately CONTEXT (client) scoped like canWriteData, so it works
  // in the web app store OUTSIDE the CMS. The global AUTH_CONFIG_WRITE is its superset in every shell: the
  // backend AppFieldVoter grants AUTH_METHOD_WRITE to an auth-config writer wherever the request comes from, so
  // only the full configuration canConfigureAuth unlocks stays CMS-only, never the method choice. Pass the app's
  // client IRI so the client-scoped grant resolves; omitting it checks the superset only. Mirrors the backend
  // AppFieldVoter (AUTH_CONFIG_WRITE OR hasAccessVia(client, AUTH_METHOD_WRITE)); its developer profile share
  // path is resolved by the App Store gates, which know the shares.
  public canChooseAuthMethod = (clientIri?: string | null): boolean => {
    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalAppWriteFieldAuthconfig,
    })) {
      return true;
    }

    if (!clientIri) {
      return false;
    }

    return this.permissionService.hasAccessVia({
      iri: clientIri,
      attribute: PermissionAttributesEnum.ContextClientAppWriteFieldAuthmethod,
    });
  };

  public canWriteStatus = (): boolean => {
    return this.hasGlobalAppPermission(PermissionAttributesEnum.GlobalAppWriteFieldStatus);
  };

  // Review-queue gate (CMS App-beoordeling): approving or rejecting a submitted app is a status write.
  public canReview = (): boolean => {
    return this.canWriteStatus();
  };

  public canWriteInternalIdentifier = (): boolean => {
    return this.hasGlobalAppPermission(PermissionAttributesEnum.GlobalAppWriteFieldInternalidentifier);
  };

  public canWriteType = (): boolean => {
    return this.hasGlobalAppPermission(PermissionAttributesEnum.GlobalAppWriteFieldType);
  };

  // Platform-admin-only marketplace-category gate (CATEGORY_WRITE), exactly like canWriteStatus /
  // canWriteType: the category an app is filed under is Verbleif-curated, never chosen by the app
  // owner/developer, so it is GLOBAL and CMS-only. Mirrors the backend AppAttribute::CATEGORY_WRITE.
  public canWriteCategory = (): boolean => {
    return this.hasGlobalAppPermission(PermissionAttributesEnum.GlobalAppWriteFieldCategory);
  };
}
